import cookies from 'js-cookie';
import { each, select, selectAll } from '../../utils/dom';
import { keyCodes } from '../../constants/keyboard';
import { delimiters } from '../../../shared/constants';
import { regex } from '../../../shared/constants/validation';
import loadingMixin from '../../../shared/vue/mixins/loading';
import { createError, removeError } from '../../../shared/formErrors';

/**
 * A11y Dialog Button component <a11y-dialog-button dialogKey="someKey"></a11y-dialog-button>
 */
Vue.component('a11y-dialog-open-button', {
  template: `
    <button class="a11y-dialog-open-button" @click="open">
      <slot></slot>
    </button>
  `,

  /**
   * Props
   * @type {string} dialogKey The proprety should match that of a modal on the page
   */
  props: ['dialogKey', 'extraData', 'textValue1', 'booleanValue1'],

  /**
   * Methods object
   */
  methods: {
    /**
     * Open function
     */
    open(event) {
      // Emit an event and pass the referenced modal by `dialogKey`.
      // Modal components registered with that `dialogKey` will open.

      if (this.dialogKey === "edit-ebill-email") {
        Event.$emit('EditPaperlessEnrollment', { ebillEmail: this.textValue1, hasMultiplePaperlessEmails: this.booleanValue1 });
      }
      Event.$emit('A11yDialogOpen', { dialogKey: this.dialogKey, extraData: this.extraData });

      // don't bubble and don't propagate
      event.stopPropagation();
      event.preventDefault();
    },
  },
});

/**
 * A11y Dialog component <a11y-dialog dialogKey="someKey"></a11y-dialog>
 */
Vue.component('a11y-dialog', {
  delimiters,
  template: `
    <div class="dialog dialog--remove" role="dialog" aria-labelledby="dialog-title" tabindex="-1"
      v-if="isOpen"
      :class="{
        'is-open': isOpen
      }
    ">
      <div class="dialog__window">
        <h2 id="dialog-title" class="dialog__title {[{ color }]}">
          {[{ title }]}
        </h2>
        <div class="dialog__content">
          <slot v-bind:booleanValue1="booleanValue1" v-bind:textValue1="textValue1" v-bind:extraData="extraData" v-bind:confirmDialog="confirmDialog"></slot>
        </div>
      </div>
    </div>
    <div class="dialog__bg"></div>
  </div>
  `,

  /**
   * Props array
   * @type {string} props.title Will show a message at top of opened modal
   * @type {string} props.dialogKey Will uniquely identify the modal on the page
   */
  props: ['title', 'dialogKey', 'extraData', 'confirmDialog', 'history', 'textValue1', 'booleanValue1'],

  /**
   * Data function
   */
  data() {
    return {
      /**
       * @type {boolean} data.isOpen
       * Used to toggle the v-if and the css class to show / hide the modal.
       */
      isOpen: false,

      /**
       * @type {boolean} data.clickOutClose
       * Whether or not to close the modal when the user clicks "outside" the modal.
       * Defaults to true
       */
      clickOutClose: true,

      /**
      * @type {HTMLElement} data.previousActiveElement
      * The element that was clicked to open the modal will be stored and recieve focus when the
      * modal closes again.
      */
      previousActiveElement: null,

      history: 'hey',

      extraData: 'data name',

      nickname: 'potato',

      goback: false,
    }
  },

  /**
   * Methods object
   */
  methods: {
    /**
     * Open modal
     */
    open() {
      this.isOpen = true;
      this.nickname = this.extraData;
      Vue.nextTick(this.afterOpen);
    },

    /**
     * Close modal
     */
    close() {
      this.isOpen = false;
      Vue.nextTick(this.afterClose);
      if (this.goback) {
        this.goback = false;
        history.back();
      }
      window.removeEventListener('popstate', this.popState);
    },

    /**
     * Assume the modal was just opened
     */
    afterOpen() {
      // set the element that opened the modal in the first place
      this.previousActiveElement = document.activeElement;

      // find the element to be focused as soon as the modal opens, if it exists
      const firstTabbable = this.$el.querySelector('.js-a11y-dialog-focus-first');

      // find the element to be focused last, if it exists
      const lastTabbable = this.$el.querySelector('.js-a11y-dialog-focus-last');

      // find all elements that can close the modal
      const closeButtons = this.$el.querySelectorAll('.js-a11y-dialog-click-close');

      // find confirm elements
      const confirmButton = this.$el.querySelector('.js-a11y-dialog-click-confirm');

      // handle first tabbable element events
      if (firstTabbable) {
        // focus the first tabbable element
        firstTabbable.focus();

        // if we're on the first tabbable item, and shift+tab, hop to the last tabbable thang
        firstTabbable.addEventListener('keydown', (event) => {
          if (event.which === keyCodes.tab && event.shiftKey) {
            event.preventDefault();
            if (lastTabbable) {
              lastTabbable.focus();
            }
          }
        });

        // handle last tabbable element events
        if (lastTabbable) {
          // if we're on the last tabbable item, hop back to the first thang
          lastTabbable.addEventListener('keydown', (event) => {
            if (event.which === keyCodes.tab && !event.shiftKey) {
              event.preventDefault();
              firstTabbable.focus();
            }
          });
        }
      } else {
        this.$el.focus();
      }

      // handle close button events
      if (closeButtons) {
        each(closeButtons, button => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.close();
          });
        });
      }

      if (confirmButton) {
        confirmButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          Event.$emit(`A11yDialogConfirm:${this.dialogKey}`);
          this.close();
        });
      }

      // everything in the document should get an aria-hidden attribute to avoid tabbing out of the modal
      each(document.body.children, child => {
        // use isEqualNode since regular comparison !== wasn't working
        if (!child.isEqualNode(this.$el) && !child.isEqualNode(this.$el.parentElement)) {
          if (child.getAttribute('aria-hidden') !== "true") {
            child.setAttribute('aria-hidden', true);

            // need to set a class to indicate this was a non-modal element and aria-hidden should
            // be removed once the modal is closed
            child.classList.add('js-aria-added');
          }
        }
      });

      // bind keydown listener for closing on esc keydown
      document.addEventListener('keydown', this.closeOnEsc.bind(this));

      // bind click listener for outside modal to close if that option wasn't overriden to false
      if (this.clickOutClose) {
        document.addEventListener('click', this.handleClickToClose.bind(this));
      }
    },

    /**
     * Assume the modal was just closed
     */
    afterClose() {
      // look only for items with class of js-aria-added (from openA11yDialog)
      each(document.querySelectorAll('.js-aria-added'), child => {
        if (child !== this.$el) {
          // could also use inert, but need polyfill - wicg-inert
          child.setAttribute('aria-hidden', false);
          child.classList.remove('js-aria-added');
        }
      });
      // Remove previously bound listener for closing on esc keydown.
      document.removeEventListener('keydown', this.closeOnEsc.bind(this));

      // unbind click out to close listener
      if (this.clickOutClose) {
        document.removeEventListener('click', this.handleClickToClose.bind(this));
      }

      // focus the previous element and reset that variable
      if (this.previousActiveElement) {
        this.previousActiveElement.focus();
        this.previousActiveElement = null;
      }
    },

    /**
     * Hanlde keyup and close modal if escape key
     */
    closeOnEsc(event) {
      if (event.keyCode === keyCodes.escape) {
        this.close();
      }
    },

    /**
     * Handle document click
     * If the clicked target is the modal itself (since it covers the whole viewport, it will be the bg)
     * and not something inside the modal itself, then we should close the modal.
     */
    handleClickToClose(event) {
      if (event.target === this.$el) {
        this.close();
      }
    },

    /**
     * Confirm dialog by emitting an event with the name of the dialog key.
     */
    confirmDialog() {
      this.$emit(`A11yDialogConfirm:${this.dialogKey}`)
    },

    popState() {
      this.goback = false;
      this.close();
    },
  },

  /**
   * Bind some listeners
   */
  created() {
    /**
     * Listen to someone wanting to open me
     * @param {object} action
     * @param {string} action.dialogKey Should match this.dialogKey to open
     */
    Event.$on('A11yDialogOpen', (action) => {
      if (action && action.dialogKey && action.dialogKey === this.dialogKey) {
        if (action.dialogKey === "edit-ebill-email") {
          this.goback = true;
          history.pushState(null, null, document.URL);
          window.addEventListener('popstate', this.popState);
        }
        this.extraData = action.extraData;
        this.open();
      }
    });
  },
  mounted() {
    if (this.dialogKey === 'update-invalid-email') {
      this.open();
    }
  },
});

/**
 * Register any app that may contain modal open buttons
 * Also register apps that might contain modals that need to be opened.
 */
const apps = selectAll('[data-vue-app="dialog-button-container"], [data-vue-app="dialog-container"]');
each(apps, appContainer => {
  new Vue({ // eslint-disable-line no-new
    el: appContainer,
  });
});
const element = select('[data-vue-app="dialog-account-removal-container"]');
if (element) {
  new Vue({
    el: element,
    mixins: [loadingMixin],
    data: {
      ebillEmail: '',
      isEBillEmailEdit: false,
      editEBillEmail: '',
      confirmEBillEmail: '',
      shouldBeDisabled: true,
      updatedEBillEmail: '',
      showError: false,
      hasEbillEmail: false,
    },
    mounted() {
      if (this.$el.dataset.hasEbillEmail && this.$el.dataset.hasEbillEmail === "True") {
        this.hasEbillEmail = true;
      }
      if (this.$el.dataset.ebillEmail) {
        this.ebillEmail = this.$el.dataset.ebillEmail;
      }
    },
    methods: {
      matchEmail() {
        removeError(this.$refs.emailMatchError, this.$refs.emailMatchError);
        this.shouldBeDisabled = true;
        if (this.confirmEBillEmail && this.editEBillEmail.trim().toLowerCase() !== this.confirmEBillEmail.trim().toLowerCase()) {
          createError(this.$refs.emailMatchError, this.$refs.emailMatchError, 'Those emails do not match. Try again');
        } else if (this.ebillEmail.trim().toLowerCase() === this.confirmEBillEmail.trim().toLowerCase() && this.hasEbillEmail) {
          createError(this.$refs.emailMatchError, this.$refs.emailMatchError, 'This email is already recorded');
        } else {
          this.shouldBeDisabled = !(regex.email.test(this.editEBillEmail.trim()) && regex.email.test(this.confirmEBillEmail.trim()));
        }
      },
      updateEBillEmail() {
        this.showError = false;
        this.confirmEBillEmail = this.confirmEBillEmail.trim();
        this.loading = true;
        Event.$emit('UpdateEBillEmail', { paperlessEmail: this.confirmEBillEmail });
      },
      reset() {
        this.isEBillEmailEdit = false;
        this.shouldBeDisabled = true;
        this.showError = false;
        this.confirmEBillEmail = '';
        removeError(this.$refs.emailMatchError, this.$refs.emailMatchError);
      },
      cancel() {
        this.updatedEBillEmail = '';
        this.reset();
      },
    },
    watch: {
      confirmEBillEmail() {
        this.matchEmail();
      },
      editEBillEmail() {
        this.matchEmail();
      },
    },
    created() {
      Event.$on('A11yDialogOpen', () => {
        this.editEBillEmail = this.ebillEmail;
        this.updatedEBillEmail = '';
        this.reset();
      });
      Event.$on('UpdateEBillEmail:callback', (success) => {
        this.loading = false;
        if (success) {
          this.updatedEBillEmail = this.confirmEBillEmail;
          this.ebillEmail = this.confirmEBillEmail;
          this.reset();
        } else {
          this.showError = true;
        }
      });
    },
  });
}
const el = select('[data-vue-app="edit-ebill-dialog-container"]');
if (el) {
  const paperlessEmailUpdateValidationCountCookieName = 'paperlessEmailUpdateValidationCount';
  new Vue({ // eslint-disable-line no-new
    el,
    mixins: [loadingMixin],
    data: {
      editEbillEmail: '',
      editPaperlessEmail: '',
      confirmEditPaperlessEmail: '',
      hasMultiplePaperlessEmails: '',
      showError: false,
      updateSuccess: false,
      emailValidationFailed: false,
      emailValidationFailedCount: 0,
    },
    mounted() {
      this.emailValidationFailedCount = cookies.get(paperlessEmailUpdateValidationCountCookieName) ? JSON.parse(cookies.get(paperlessEmailUpdateValidationCountCookieName)) : 0;
    },
    created() {
      Event.$on('EditPaperlessEnrollment', (action) => {
        this.editEbillEmail = action.ebillEmail;
        this.editPaperlessEmail = '';
        this.hasMultiplePaperlessEmails = action.hasMultiplePaperlessEmails;
        this.confirmEditPaperlessEmail = '';
        this.loading = false;
        this.showError = false;
        this.updateSuccess = false;
      });
      Event.$on('ReturnEditPaperlessEnrollment', (response) => {
        this.loading = false;
        if (response) {
          this.updateSuccess = response.success;
          this.showError = !response.success;
        }
        console.log(response.err)
        if (response.err != null && response.err.response != null && response.err.response.status === 400 && response.err.response.data.error.code === 1060) {
          this.showError = false;
          this.emailValidationFailed = true;
          this.emailValidationFailedCount = this.emailValidationFailedCount + 1;
          cookies.set(paperlessEmailUpdateValidationCountCookieName, this.emailValidationFailedCount);
        }
      });
    },
    computed: {
      emailValidationFailedThresholdLimitReached() {
        return this.emailValidationFailedCount >= 3;
      },
    },
    methods: {
      updatePaperless() {
        let hasError = false;
        removeError(this.$refs.editPaperlessEmail, this.$refs.editPaperlessEmailError);
        removeError(this.$refs.confirmEditPaperlessEmail, this.$refs.confirmPaperlessEmailError);

        if (!this.editPaperlessEmail) {
          createError(this.$refs.editPaperlessEmail, this.$refs.editPaperlessEmailError, 'Please enter an email address.');
          hasError = true;
        } else if (!this.confirmEditPaperlessEmail) {
          createError(this.$refs.confirmEditPaperlessEmail, this.$refs.confirmPaperlessEmailError, 'Please re-enter the email address.');
          hasError = true;
        } else if (this.editPaperlessEmail && this.confirmEditPaperlessEmail) {
          if (this.confirmEditPaperlessEmail.toLowerCase() !== this.editPaperlessEmail.toLowerCase()) {
            createError(this.$refs.confirmEditPaperlessEmail, this.$refs.confirmPaperlessEmailError, 'Those emails do not match. Try again.');
            hasError = true;
          } else if (this.editEbillEmail.toLowerCase() === this.editPaperlessEmail.toLowerCase()) {
            createError(this.$refs.confirmEditPaperlessEmail, this.$refs.confirmPaperlessEmailError, 'You\'re already enrolled to receive your Paperless bill at this email address. Please enter a different email address.');
            hasError = true;
          }
        }

        if (!hasError) {
          this.loading = true;
          this.emailValidationFailed = false;
          Event.$emit('UpdatePaperlessEnrollment', { paperlessEmail: this.editPaperlessEmail, emailValidationFailedCount: this.emailValidationFailedCount });
        }
      },
    },
  });
}
const autopayEl = select('[data-vue-app="autopay-update-one-time-payment-dialog-container"]');
if (autopayEl) {
  new Vue({ // eslint-disable-line no-new
    el: autopayEl,
    methods: {
      updateOneTimePayment(updateExistingOneTimePayment) {
        Event.$emit('UpdateAutopayUpdateExistingOneTimePayment', { updateExistingOneTimePayment });
      },
    },
  });
}

const removeBankAccountEl = select('[data-vue-app="remove-bank-account-dialog-container"]');
if (removeBankAccountEl) {
  new Vue({ // eslint-disable-line no-new
    el: removeBankAccountEl,
    mixins: [loadingMixin],
    data: {
      bankAccount: null,
      removeSuccess: false,
      step: 1,
      loading: false,
    },
    mounted() {
      this.bankAccount = {
        enrolledInAutoPay: false,
        pendingOneTime: false,
        accountNumber: '',
        accountType: '',
      };
    },
    created() {
      Event.$on('UpdateBankAccountToRemove', (bankAccount) => {
        this.removeSuccess = false;
        this.step = 1;
        if (bankAccount.bankAccount) {
          this.bankAccount = bankAccount.bankAccount;
        }
      });
      Event.$on('UpdateBankAccountToRemoveCompleted', (data) => {
        this.loading = false;
        this.step = 2;
        this.removeSuccess = data.removeSuccess;
      });
    },
    methods: {
      removeBankAccount() {
        this.loading = true;
        Event.$emit('ConfirmRemoveBankAccount', { bankAccount: this.bankAccount });
      },
    },
  });
}

const updateOneTimePaymentEl = select('[data-vue-app="update-one-time-payment-dialog-container"]');
if (updateOneTimePaymentEl) {
  new Vue({ // eslint-disable-line no-new
    el: updateOneTimePaymentEl,
    methods: {
      updateOneTimePayment(updateOneTimePayment) {
        Event.$emit('updateOneTimePayment', { updateOneTimePayment });
      },
    },
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/shared/vue/components/dialog.js